'use client';

import { useCallback, useEffect, useMemo } from 'react';
import { usePathname } from 'next/navigation';
import { ActionType } from '@diritto/common/app/inapp-event.constant';
import { WebViewScreen } from '@diritto/common/app/webview-header.constant';
import { AgeRestriction, RelayType } from '@diritto/common/content/constants';
import {
  ClickAction,
  ClickTargetType,
  TrackerExtraInfo,
  TrackerPosition,
  TrackingEventType,
} from '@diritto/common/tracker/tracking-event.constant';
import { ContestId } from '@diritto/common/content/contest.constants';
import { ContentFavoriteAndEpisodeNotiButton } from 'app-components/contents/ContentFavoriteAndEpisodeNotiButton';
import { ContentDetailView } from 'app-components/contents/detail/ContentDetail';
import { ContentDetailJsonLd } from 'app-components/contents/detail/ContentDetailJsonLd';
import { ProposalBanner } from 'app-components/contents/detail/ProposalBanner';
import { DirittoStudioBanner } from 'app-components/contents/DirittoStudioBanner';
import { useAuthState } from 'contexts/AuthProvider';
import { useInAppState } from 'contexts/InAppProvider';
import { useTrackingState } from 'contexts/TrackingProvider';
import { usePostRelayContentQuery } from 'queries/relay/usePostRelayContentQuery';
import { useShowNpsCampaignModal } from 'state-hooks/modal/useShowNpsCampaignModal';
import { BlockContents } from 'ui-components/common/BlockContents';
import { FullPageLoading } from 'ui-components/common/Loading';
import ErrorView from 'ui-components/error/ErrorView';
import { MutableHeaderLayout } from 'ui-components/layout/MutableHeaderLayout';
import { ContentCoverModal } from 'ui-components/modals/CoverModal';
import { Modal } from 'ui-components/modals/Modal';
import { NpsCampaignModal } from 'ui-components/modals/NpsCampaignModal';
import { CommentLinkButton } from 'ui-components/navigations/HeaderButtons';
import { useInAppHeader } from 'ui-hooks/useInAppHeader';
import { useInAppStatusBar } from 'ui-hooks/useInAppStatusBar';
import { useShare } from 'ui-hooks/useShare';
import { AccessBlockCode } from 'utils/checkAccessBlock';
import { configure } from 'utils/configure';
import ContentDetailPrivatePage from 'app-components/contents/detail/ContentDetailPrivatePage';
import { useActiveContentContestIdsDispatch } from '@/contexts/ActiveContentContestIdsProvider';

type ContentPageProps = {
  contentId: number;
  isApp: boolean;
  userAgent: string;
};

const trackerExtraInfo: TrackerExtraInfo = {
  trackerPosition: TrackerPosition.ContentDetail,
};

export default function ContentPage({ contentId, isApp }: ContentPageProps) {
  useInAppHeader({ screen: WebViewScreen.ContentDetail, contentId });
  useInAppStatusBar(WebViewScreen.ContentDetail);
  const { checkActiveContentContests } = useActiveContentContestIdsDispatch();

  const requestShare = useShare();
  const { addActionEventListener } = useInAppState();
  const { profile, isLoggedIn, user } = useAuthState();

  const pathname = usePathname();
  const thisUrl = `${configure.serviceUrl}${pathname}`;

  const { tracker } = useTrackingState();
  const handleTrack = useCallback(
    (action: ClickAction, clickType: ClickTargetType) => {
      tracker.track(TrackingEventType.Click, { action, clickType, trackerExtraInfo });
    },
    [tracker],
  );

  const { isFetched: isContentFetched, data: contentData } = usePostRelayContentQuery(contentId);
  const contentPublic = contentData?.contentPublic;
  const authorProfilePublic = contentData?.authorProfilePublic;

  useEffect(() => {
    if (contentPublic && contentPublic.contentContestId) {
      checkActiveContentContests(ContestId.Contest2026, [contentPublic.contentContestId]);
    }
  }, [contentPublic, checkActiveContentContests]);

  const {
    showNpsCampaignModal,
    needShowNpsCampaignModal,
    isPrepared: isNpsCampaignPrepared,
  } = useShowNpsCampaignModal();

  const handleShare = useCallback(
    () => contentPublic && requestShare({ title: contentPublic.title, text: contentPublic.title, url: thisUrl }),
    [contentPublic, requestShare, thisUrl],
  );

  const hiddenMenu = useMemo(
    () => [
      {
        title: '공유하기',
        onClick: handleShare,
      },
    ],
    [handleShare],
  );

  useEffect(
    () =>
      addActionEventListener(params => {
        switch (params.type) {
          case ActionType.Share: {
            handleShare();
            break;
          }
          default:
            break;
        }
      }),
    [addActionEventListener, authorProfilePublic, contentPublic, handleShare, requestShare],
  );

  useEffect(() => {
    if (isNpsCampaignPrepared) {
      if (needShowNpsCampaignModal) {
        showNpsCampaignModal();
      }
    }
  }, [isNpsCampaignPrepared, needShowNpsCampaignModal, showNpsCampaignModal]);

  const rightButtons = useMemo(
    () => [
      <ContentFavoriteAndEpisodeNotiButton
        key="favoriteAndEpisodeNoti"
        contentId={contentId}
        trackerExtraInfo={trackerExtraInfo}
      />,
      <CommentLinkButton key="reply" href={`/contents/${contentId}/replies`} />,
    ],
    [contentId],
  );

  if (!isContentFetched) {
    return (
      <MutableHeaderLayout isApp={isApp}>
        <FullPageLoading />
      </MutableHeaderLayout>
    );
  }

  if (!contentPublic) {
    if (contentData?.content.relayType === RelayType.Private) {
      return <ContentDetailPrivatePage contentId={contentId} isApp={isApp} />;
    }
    return (
      <MutableHeaderLayout isApp={isApp}>
        <ErrorView title="삭제된 작품이에요." description="이 작품은 더 이상 볼 수 없어요." />
      </MutableHeaderLayout>
    );
  }

  if (!isLoggedIn && contentPublic.ageRestriction === AgeRestriction.Adult) {
    return (
      <>
        <MutableHeaderLayout isApp={isApp}>
          <BlockContents accessBlockCode={AccessBlockCode.AuthorizationAdultRequired} />
        </MutableHeaderLayout>
        <ContentDetailJsonLd content={contentPublic} authorProfile={authorProfilePublic} />
      </>
    );
  }

  if (!user?.isAdult && contentPublic.ageRestriction === AgeRestriction.Adult) {
    return (
      <>
        <MutableHeaderLayout isApp={isApp}>
          <BlockContents accessBlockCode={AccessBlockCode.AdultRequired} />
        </MutableHeaderLayout>
        <ContentDetailJsonLd content={contentPublic} authorProfile={authorProfilePublic} />
      </>
    );
  }

  return (
    <>
      <MutableHeaderLayout rightButtons={rightButtons} hiddenMenu={hiddenMenu} isApp={isApp} showTopUp>
        <>
          {contentPublic.profileId !== undefined && contentPublic.profileId === profile?.id && (
            <DirittoStudioBanner
              href={`/studio/contents/${contentId}`}
              onClick={() => handleTrack(ClickAction.ToDirittoStudioContentEpisodes, ClickTargetType.Banner)}
            />
          )}
          <ProposalBanner content={contentPublic} />
          <ContentDetailView
            content={contentPublic}
            authorProfile={authorProfilePublic}
            trackerExtraInfo={trackerExtraInfo}
          />
        </>
      </MutableHeaderLayout>
      <Modal useBody>
        <ContentCoverModal />
      </Modal>
      <Modal>
        <NpsCampaignModal />
      </Modal>
      <ContentDetailJsonLd content={contentPublic} authorProfile={authorProfilePublic} />
    </>
  );
}
